import {
	MouseEvent,
	ReactElement,
	useCallback,
	useContext,
	useMemo,
	KeyboardEvent
} from "react";
import {
	Icon,
	IconValue,
	isSvgIcon,
	unsetIconColor
} from "../Icon/Icon";
import styles from "./GridItemPreviewContent.module.less";
import classNames from "classnames";
import { GridItemContext } from "./GridItem";
import { Checkbox } from "../Checkbox/Checkbox";
import { GridItemCheckbox } from "./GridItemContent";
import { ActionAndOthers } from "client_legacy/ndaw/actions";
import {
	actionComponentFactory,
	computeActionsToRender
} from "../ActionList/actionsRenderHelpers";
import { Key } from "client/Misc/commonTypes";
import { useKeyboardListNavigationItem } from "client/Hooks";
import { GridItemMoreActions } from "./GridItemMoreActions";

export type GridItemPreviewContentProps<TActionContext> = {
	name: string;
	thumbnail?: ReactElement;
	icons?: readonly IconValue[];
	info?: string | ReactElement;
	hasImage?: boolean;
	checkbox?: GridItemCheckbox;
	onTitleClick?(): void;
} & ({
	actions?: undefined;
} | {
	actions: readonly ActionAndOthers<TActionContext>[];
	actionContext: TActionContext;
});

export const GridItemPreviewContent = <TActionContext, >(props: GridItemPreviewContentProps<TActionContext>): ReactElement => {
	const {
		name,
		icons,
		thumbnail,
		hasImage,
		info,
		onTitleClick
	} = props;

	const {
		isInteracting,
		isDisabled,
		isFocused
	} = useContext(GridItemContext);

	const titleClickHandler = useCallback(
		(args: MouseEvent): void => {
			if (onTitleClick && !isDisabled) {
				args.stopPropagation();
				onTitleClick();
			}
		},
		[
			onTitleClick,
			isDisabled
		]
	);

	const actionsToRender = useMemo(
		(): readonly ActionAndOthers<TActionContext>[] | null => {
				if (!props.actions) {
					return null;
				}

				return computeActionsToRender(
					props.actions,
					props.actionContext
				);
			},
		[props]
	);

	return (
		<>
			<div
				className={classNames(
					styles.root,
					{
						[styles.root_isInteracting]: isInteracting || isFocused,
						[styles.root_disabled]: isDisabled
					}
				)}
			>
				<div className={styles.tools}>
					{props.actions !== undefined
						&& actionsToRender
						&& actionsToRender.length > 0
						&& (
							<GridItemMoreActions
								menuTitle={name}
								actions={actionsToRender}
								actionContext={props.actionContext}
								isWhite={hasImage}
								tabIndex={isFocused ? 0 : -1}
							/>
						)
					}
					{props.checkbox && (
						<Checkbox
							value={props.checkbox.isChecked}
							onChange={props.checkbox.onIsCheckChange}
							shouldBubbleClick={false}
							tabIndex={isFocused ? 0 : -1}
						/>
					)}
				</div>
				<div
					className={classNames(
						styles.preview,
						{[styles.preview_hasImage]: hasImage}
					)}
					onClick={titleClickHandler}
				>
					{thumbnail}
				</div>
				<h4
					className={classNames(
						styles.name,
						{[styles.name_isClickable]: onTitleClick && !isDisabled}
					)}
					onClick={titleClickHandler}
					title={name}
				>
					{icons && icons.map(
						(x, i) => (
							<span
								key={i}
								className={classNames(
									styles.icon,
									{ [styles.icon_svg]: isSvgIcon(x)}
								)}
							>
								<Icon
									value={isDisabled
										? unsetIconColor(x)
										: x
									}
								/>
							</span>
						)
					)}
					{name}
				</h4>
				{info && (
					<div className={styles.info}>
						{info}
					</div>
				)}
			</div>
		</>
	);
};

GridItemPreviewContent.displayName = "GridItemPreviewContent";

type NavigableActionItemProps<TActionContext> = {
	action: ActionAndOthers<TActionContext>;
	actionContext: TActionContext;
	index: number;
	onCloseMenu: () => void;
};

const NavigableActionItem = <TActionContext, >(
	props: NavigableActionItemProps<TActionContext>
): ReactElement => {
	const {
		action,
		actionContext,
		index,
		onCloseMenu
	} = props;

	const {
		onKeyDown,
		ref
	} = useKeyboardListNavigationItem({
		itemIndex: index,
		onExecute: (): void => {
			if (action.canExecute?.(actionContext)) {
				action.execute?.(actionContext);
				onCloseMenu();
			}
		},
		executeKey: [
			Key.Enter,
			Key.Space
		],
		shouldCancelOnTab: true
	});

	const KeyDownHandler = useCallback(
		(args: KeyboardEvent<HTMLLIElement>): void => {
			onKeyDown(args);
			args.stopPropagation();
		},
		[onKeyDown]
	);

	return actionComponentFactory(
		action,
		actionContext,
		{
			ref: ref,
			onKeyDown: KeyDownHandler,
			tabIndex: -1
		}
	);
};
NavigableActionItem.displayName = "NavigableActionItem";